import React, { useMemo, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { MdDelete, MdAdd, MdEdit } from 'react-icons/md';
import DeleteModel from '../Common/DeleteModel';
import { useAuth } from '../../Context/AuthContext';
import EducationModal from './EducationModal';
import ExamModal from './ExamModal';
import { getAllProviderEducations, deleteProviderEducation, getAllProviderExams, saveProviderExam, deleteProviderExam } from '../../Services/ProviderService';

const validationSchema = Yup.object({
  educationTraining: Yup.array().of(
    Yup.object({
      id: Yup.string().nullable(),
      education_type: Yup.number().nullable(),
      education_type_name: Yup.string().trim().nullable(),
      institution_name: Yup.string().trim().nullable(),
      specialty: Yup.string().trim().nullable(),
      degree_earned: Yup.number().nullable(),
      degree_earned_name: Yup.string().trim().nullable(),
      program_completed: Yup.number().nullable(),
      program_completed_name: Yup.string().trim().nullable(),
      start_date: Yup.date().nullable().typeError('Invalid date format'),
      end_date: Yup.date().nullable().typeError('Invalid date format'),
      program_director_name: Yup.string().trim().nullable(),
      program_director_email: Yup.string().email().nullable(),
      program_director_phone: Yup.string().trim().nullable(),
      status_id: Yup.number().nullable(),
      organization_name: Yup.string().trim().nullable(),
      address: Yup.object().nullable(),
    })
  ),
  exams: Yup.array().of(
    Yup.object({
      id: Yup.string().nullable(),
      provider_id: Yup.string().nullable(),
      status_id: Yup.number().nullable(),
      file_id: Yup.number().nullable(),
      exam_type: Yup.number().nullable(),
      first_try_date: Yup.date().nullable().typeError('Invalid date format'),
      exam_date: Yup.date().nullable().typeError('Invalid date format'),
      exam_passed: Yup.boolean().nullable(),
      score: Yup.string().trim().nullable(),
      no_of_attempts: Yup.number().nullable(),
      location: Yup.string().trim().nullable(),
    })
  ),
});

const EDUCATION_TYPE_OPTIONS = [
  { id: 1, label: 'Medical School' },
  { id: 2, label: 'Residency' },
  { id: 3, label: 'Fellowship' },
  { id: 4, label: 'Internship' },
  { id: 5, label: 'Other' },
];

const DEGREE_OPTIONS = [
  { id: 1, label: 'MD' },
  { id: 2, label: 'DO' },
  { id: 3, label: 'PhD' },
  { id: 4, label: 'DDS' },
  { id: 5, label: 'Other' },
];

const emptyExamItem = {
  id: null,
  exam_name: '',
  exam_date: '',
  score: '',
};

const emptyForm = {
  educationTraining: [],
  exams: [],
};

const getOrganizationId = (provider) =>
  provider?.organization_id ??
  provider?.organizationId ??
  provider?.OrganizationId ??
  provider?.org_id ??
  provider?.organization?.id ??
  null;

const normalizeEducationDocument = (item) => ({
  ...item,
  document:
    item.document ??
    item.file_name ??
    item.fileName ??
    item.document_name ??
    item.documentName ??
    (item.file && (item.file.name || item.file.fileName || item.file.file_name)) ??
    null,
});

const mapApiToForm = (providerData, loadedEducationTraining, loadedExams) => {
  return {
    educationTraining: Array.isArray(providerData?.educationTraining)
      ? providerData.educationTraining.map(normalizeEducationDocument)
      : Array.isArray(loadedEducationTraining)
        ? loadedEducationTraining.map(normalizeEducationDocument)
        : [],
    exams: Array.isArray(providerData?.exams)
      ? providerData.exams
      : Array.isArray(loadedExams)
        ? loadedExams.map(normalizeEducationDocument)
        : [],
  };
};

export default function EducationTraining({
  initialData,
  providerId,
  onSaveSuccess,
  onContinue,
}) {
  const { token } = useAuth();
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [editingEducationIndex, setEditingEducationIndex] = useState(null);
  const [editingExamIndex, setEditingExamIndex] = useState(null);
  const [showExamModal, setShowExamModal] = useState(false);
  const [loadedEducationTraining, setLoadedEducationTraining] = useState([]);
  const [loadedExams, setLoadedExams] = useState([]);
  const [educationLoading, setEducationLoading] = useState(false);
  const [educationSaving, setEducationSaving] = useState(false);
  const [savingExam, setSavingExam] = useState(false);
  const [examLoading, setExamLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, index: null, item: null, isLoading: false });

  const organizationId = getOrganizationId(initialData);

  const initialFormValues = useMemo(() => {
    return initialData
      ? mapApiToForm(initialData, loadedEducationTraining, loadedExams)
      : mapApiToForm(null, loadedEducationTraining, loadedExams);
  }, [initialData, loadedEducationTraining, loadedExams]);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (onContinue) {
          onContinue(values);
        }
      } catch (error) {
        console.error(error);
        toast.error(error.message || 'Failed to save education information');
      }
    },
  });

  const loadEducations = async () => {
    if (!token || !providerId) return [];

    setEducationLoading(true);
    try {
      const response = await getAllProviderEducations(token, providerId);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data.map(normalizeEducationDocument) : [];
        setLoadedEducationTraining(refreshed);
        formik.setFieldValue('educationTraining', refreshed);
        return refreshed;
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setLoadedEducationTraining([]);
      } else {
        // toast.error(response?.message || 'Failed to load education records');
        setLoadedEducationTraining([]);
      }
      return [];
    } catch (error) {
      console.error('Error loading provider educations:', error);
      return [];
    } finally {
      setEducationLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !providerId) return;
    if (initialData?.educationTraining?.length > 0) return;

    let isMounted = true;
    const loadData = async () => {
      if (!isMounted) return;
      await loadEducations();
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [token, providerId, initialData]);

  const loadExams = async () => {
    if (!token || !providerId) return [];

    setExamLoading(true);
    try {
      const response = await getAllProviderExams(token, providerId);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setLoadedExams(refreshed);
        formik.setFieldValue('exams', refreshed);
        return refreshed;
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setLoadedExams([]);
      } else {
        // toast.error(response?.message || 'Failed to load exam records');
        setLoadedExams([]);
      }
    } catch (error) {
      console.error('Error loading provider exams:', error);
      toast.error('Failed to load exam records.');
    } finally {
      setExamLoading(false);
    }
    return [];
  };

  useEffect(() => {
    if (!token || !providerId) return;
    if (initialData?.exams?.length > 0) return;

    let isMounted = true;
    (async () => {
      if (!isMounted) return;
      await loadExams();
    })();
    return () => {
      isMounted = false;
    };
  }, [token, providerId, initialData]);

  const handleAddEducation = () => {
    setEditingEducationIndex(null);
    setShowEducationModal(true);
  };

  const handleEditEducation = (index) => {
    setEditingEducationIndex(index);
    setShowEducationModal(true);
  };

  const handleSaveEducation = async (values) => {
    setEducationSaving(true);
    if (!token || !providerId) {
      try {
        const updated = [...formik.values.educationTraining];
        if (editingEducationIndex !== null) {
          updated[editingEducationIndex] = values;
        } else {
          updated.push(values);
        }
        formik.setFieldValue('educationTraining', updated);
        setShowEducationModal(false);
        setEditingEducationIndex(null);
        toast.success('Education saved successfully');
      } catch (error) {
        toast.error('Failed to save education record');
      } finally {
        setEducationSaving(false);
      }
      return;
    }

    try {
      const refreshed = await loadEducations();
      if (!refreshed) {
        toast.error('Failed to refresh education records after save');
        return;
      }
      setShowEducationModal(false);
      setEditingEducationIndex(null);
      toast.success(
        editingEducationIndex !== null
          ? 'Education record updated'
          : 'Education record added'
      );
    } catch (error) {
      console.error('Error refreshing education records:', error);
      toast.error('Failed to refresh education records after save');
    } finally {
      setEducationSaving(false);
    }
  };

  const handleDeleteEducation = (index) => {
    const item = formik.values.educationTraining[index];
    const optimisticUpdate = (updated) => {
      formik.setFieldValue('educationTraining', updated);
    };

    (async () => {
      if (token && item && (item.id || item.Id)) {
        try {
          const id = item.id || item.Id;
          const resp = await deleteProviderEducation(token, id);
          if (resp?.is_success) {
            const refreshed = await loadEducations();
            if (Array.isArray(refreshed)) {
              optimisticUpdate(refreshed);
            } else {
              const updated = formik.values.educationTraining.filter((_, i) => i !== index);
              optimisticUpdate(updated);
            }
            toast.success(resp?.message || 'Education record deleted successfully');
            return;
          } else if (resp?.is_success === false && resp?.status_code === 500) {
            toast.error(resp?.message || 'Internal Server Error. Please Contact Support Team.');
          } else {
            toast.error(resp?.message || 'Failed to delete education record');
          }
        } catch (err) {
          console.error('Error deleting education record:', err);
          toast.error('Failed to delete education record');
        }
      } else {
        const updated = formik.values.educationTraining.filter((_, i) => i !== index);
        optimisticUpdate(updated);
        toast.success('Education record deleted');
      }
    })();
  };

  const handleAddExam = () => {
    setEditingExamIndex(null);
    setShowExamModal(true);
  };

  const handleEditExam = (index) => {
    setEditingExamIndex(index);
    setShowExamModal(true);
  };

  const handleSaveExam = async (values) => {
    const payload = {
      ...values,
      provider_id: providerId || values.provider_id,
      organization_id: organizationId || values.organization_id,
      type: values.id ? 'update' : 'create',
    };

    if (!token) {
      toast.error('Unable to save exam without authentication.');
      return;
    }

    if (!providerId) {
      const updated = [...formik.values.exams];
      if (editingExamIndex !== null) {
        updated[editingExamIndex] = values;
      } else {
        updated.push(values);
      }
      formik.setFieldValue('exams', updated);
      setShowExamModal(false);
      setEditingExamIndex(null);
      toast.success('Provider ID missing. Exam saved locally until provider is created.');
      return;
    }

    setSavingExam(true);
    try {
      const response = await saveProviderExam(token, payload);
      if (response?.is_success) {
        const refreshed = await loadExams();
        setShowExamModal(false);
        setEditingExamIndex(null);
        toast.success(response?.message || "Exam saved successfully");
      } else if (!response?.is_success && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save exam');
      }
    } catch (error) {
      console.error('Error saving provider exam:', error);
      toast.error(error?.message || 'Failed to save exam');
    } finally {
      setSavingExam(false);
    }
  };

  const openDeleteModal = (type, index) => {
    const items = type === 'education' ? formik.values.educationTraining : formik.values.exams;
    setDeleteModal({ isOpen: true, type, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, type: null, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.type === null || deleteModal.index === null || deleteModal.index === undefined) return;

    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      if (deleteModal.type === 'education') {
        await handleDeleteEducation(deleteModal.index);
      } else {
        await handleDeleteExam(deleteModal.index);
      }
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDeleteExam = async (index) => {
    const item = formik.values.exams[index];
    const optimisticUpdate = (updated) => formik.setFieldValue('exams', updated);

    if (token && item && (item.id || item.Id)) {
      try {
        const id = item.id || item.Id;
        const resp = await deleteProviderExam(token, id);
        if (resp?.is_success) {
          const refreshed = await loadExams();
          if (Array.isArray(refreshed)) {
            optimisticUpdate(refreshed);
          } else {
            const updated = formik.values.exams.filter((_, i) => i !== index);
            optimisticUpdate(updated);
          }
          setEditingExamIndex(null);
          toast.success(resp?.message || 'Exam deleted successfully');
          return;
        } else if (resp?.is_success === false && resp?.status_code === 500) {
          toast.error("Internal Server Error. Please Contact Support Team.");
        } else{
          toast.error(resp?.message || 'Failed to delete exam');
        }
      } catch (err) {
        console.error('Error deleting exam:', err);
        toast.error('Failed to delete exam');
      }
    } else {
      const updated = formik.values.exams.filter((_, i) => i !== index);
      optimisticUpdate(updated);
      setEditingExamIndex(null);
    }
  };

  const getEducationTypeLabel = (item) => {
    return item.education_type_name || EDUCATION_TYPE_OPTIONS.find((opt) => opt.id === item.education_type)?.label || 'N/A';
  };

  const getDegreeLabel = (item) => {
    return item.degree_earned_name || DEGREE_OPTIONS.find((opt) => opt.id === item.degree_earned)?.label || 'N/A';
  };

  return (
    <div className="container-fluid p-0">
      <EducationModal
        isOpen={showEducationModal}
        onClose={() => {
          setShowEducationModal(false);
          setEditingEducationIndex(null);
        }}
        onSave={handleSaveEducation}
        initialData={
          editingEducationIndex !== null
            ? formik.values.educationTraining[editingEducationIndex]
            : null
        }
        providerId={providerId}
        isLoading={educationSaving}
      />
      <ExamModal
        isOpen={showExamModal}
        onClose={() => {
          setShowExamModal(false);
          setEditingExamIndex(null);
        }}
        onSave={handleSaveExam}
        initialData={
          editingExamIndex !== null ? formik.values.exams[editingExamIndex] : null
        }
        providerId={providerId}
        organizationId={organizationId}
        isLoading={savingExam}
      />

      <form onSubmit={formik.handleSubmit}>
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold hc-text-primary m-0">Education & Training</h6>
            <button
              type="button"
              className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
              onClick={handleAddEducation}
            >
              <MdAdd size={18} />
              Add
            </button>
          </div>

          {educationLoading ?(
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : formik.values.educationTraining.length === 0 ? (
            <div className="text-center py-4 text-muted small">
              No education records added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row g-3 mb-3">
            {
              formik.values.educationTraining.map((item, index) => (
                <React.Fragment key={index}>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Education Type
                    </div>

                    <div className="fw-semibold text-dark text-truncate"
                      data-bs-toggle="tooltip" 
                     data-bs-placement="top" 
                    title={getEducationTypeLabel(item)}
                    >
                      {getEducationTypeLabel(item)}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Institution
                    </div>

                    <div className="fw-semibold text-dark text-truncate"
                     data-bs-toggle="tooltip" 
                    data-bs-placement="top" 
                    title={item.institution_name}>
                     {item.institution_name}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Degree
                    </div>


                    <div className="fw-semibold text-dark text-truncate"  
                    data-bs-toggle="tooltip" 
                    data-bs-placement="top" 
                    title={getDegreeLabel(item)}>
                     {getDegreeLabel(item)}
                     
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                      <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-3"
                            onClick={() => handleEditEducation(index)}
                          >
                            <MdEdit size={16} />
                          </button>

                      <button
                            type="button"
                            className="btn btn-sm btn-danger rounded-3"
                            onClick={() => openDeleteModal('education', index)}
                          >
                            <MdDelete size={16} />
                          </button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
             

            </div>

            )}

        </div>

        {/* Exams Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold hc-text-primary m-0">Exams</h6>
            <button
              type="button"
              className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
              onClick={handleAddExam}
            >
              <MdAdd size={18} />
              Add
            </button>
          </div>


          {examLoading ?(
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : formik.values.exams.length === 0 ? (
            <div className="text-center py-4 text-muted small">
              No Exam records added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row g-3 mb-3">
            {
              formik.values.exams.map((item, index) => (
                <React.Fragment key={index}>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Exam Name
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.exam_type_name}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Exam Date
                    </div>

                    <div className="fw-semibold text-dark">
                     {item.exam_date}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Score
                    </div>

                    <div className="fw-semibold text-dark">
                     {item.score}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                      <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-3"
                            onClick={() => handleEditExam(index)}
                          >
                            <MdEdit size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger rounded-3"
                            onClick={() => openDeleteModal('exam', index)}
                          >
                            <MdDelete size={16} />
                          </button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
             

            </div>

            )}


        </div>
        <div className="text-end mt-4">
          <button
            type="submit"
            className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Continue
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </form>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel={deleteModal.type === 'exam' ? 'exam' : 'education record'}
        itemName={deleteModal.item ? (deleteModal.type === 'exam'
          ? deleteModal.item.exam_name || deleteModal.item.exam_type_name || 'this exam'
          : deleteModal.item.institution_name || deleteModal.item.education_type_name || 'this education record')
          : deleteModal.type === 'exam' ? 'this exam' : 'this education record'}
        description={deleteModal.type === 'exam'
          ? 'This will permanently delete this exam record. This action cannot be undone.'
          : 'This will permanently delete this education record. This action cannot be undone.'}
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />
    </div>
  );
}
